import { Button, IconButton, Stack } from "@chakra-ui/react";
import clsx from "clsx";
import { useEffect, useMemo, useRef } from "react";
import { MdHistory } from "react-icons/md";
import { MdsEditOffRound } from "react-icons-with-materialsymbols/mds";
import { useParams } from "react-router-dom";
import { Edge, Node, useReactFlow, Viewport } from "reactflow";

import { ToastType, useShowToast } from "@/components/toast";
import usePermissions from "@/hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "@/reduxHooks";
import { CATEGORY, PERMISSIONS } from "@/utils/enums";

import {
  useEndEditAccessMutation,
  useGetWorkflowRunStatusQuery,
  useSaveWorkflowMutation,
} from "../../api";
import { useWorkflowActions } from "../../hooks/useWorkflowActions";
import {
  currentWfActiveUser,
  currentWorkflowId,
  currentWorkflowRunId,
  currentWorkflowVersionTagId,
  getEditingAllowed,
  hideAllPanels,
  isPreviewMode,
  setCurrentWfActiveUser,
  setEditingAllowed,
  setPreviewMode,
  setReferenceRunId,
  setWorkflowRunId,
  setWorkflowRunStatus,
  setworkflowStatus,
  setWorkflowVersionTagId,
  showPanel,
  workflowRunningStatus,
} from "../../redux";
import { WorkflowStatusSchema } from "../../types";
import {
  ConvertFlowtoWorkflowPayload,
  NODE_STATUS,
  WORKFLOW_PANELS,
} from "../../utils";

import PreviewModeOptions from "./preview-mode-options";
import { RunAndLogButtons } from "./run-log-buttons";
import ViewModeOptions from "./view-mode-options";

export const StatusBar = ({
  nodes,
  edges,
}: {
  nodes: Node[];
  edges: Edge[];
}) => {
  const params = useParams();
  const isEditingAllowed = useAppSelector(getEditingAllowed);
  const wfRunStatus = useAppSelector(workflowRunningStatus);
  const currentUser = useAppSelector(currentWfActiveUser);
  const workflowId = useAppSelector(currentWorkflowId);
  const workflowRunId = useAppSelector(currentWorkflowRunId);
  const workflowVersionTagId = useAppSelector(currentWorkflowVersionTagId);

  const isPreviewModeOn = useAppSelector(isPreviewMode);
  const { toObject } = useReactFlow();
  const { fetchAndUpdateWorkflow, wfMetaData } = useWorkflowActions();

  const { checkPermission } = usePermissions();
  const hasEditAccess = checkPermission({
    requiredPermission: PERMISSIONS.WRITE,
    type: CATEGORY.Analysis,
    id: params["analysisId"]!,
  });
  const failureCountRef = useRef(0);

  const dispatch = useAppDispatch();
  const toast = useShowToast(undefined, undefined, true);

  const [endEditAccess, { isLoading: isEnding }] = useEndEditAccessMutation();
  const [saveWorkflow, { isLoading: isSaving }] = useSaveWorkflowMutation();

  const isLoading = isSaving || isEnding;

  const isCurrentUserEditing = currentUser?.isCurrentUser;
  const isWorkflowRunning =
    wfRunStatus === NODE_STATUS.RUNNING ||
    wfRunStatus === NODE_STATUS.CANCELLING;

  const showLockedMessage = useMemo(() => {
    return !isWorkflowRunning && !isCurrentUserEditing && !isEditingAllowed;
  }, [isWorkflowRunning, isCurrentUserEditing, isEditingAllowed]);

  const isWorkflowPartiallyRunning =
    wfRunStatus === NODE_STATUS.PARTIALLY_RUNNING;

  const disableHistoryButton = useMemo(() => {
    return isWorkflowRunning || isWorkflowPartiallyRunning;
  }, [isWorkflowRunning, isWorkflowPartiallyRunning]);

  const shouldSkip =
    workflowId !== undefined &&
    workflowId !== null &&
    !isWorkflowRunning &&
    !isWorkflowPartiallyRunning;

  const {
    data: userData,
    error,
    isError,
  } = useGetWorkflowRunStatusQuery(
    { workflowId: workflowId!, workflowRunId: workflowRunId ?? "" },
    {
      skip: shouldSkip,
      pollingInterval: 2000,
    }
  );

  // update store on data change
  useEffect(() => {
    if (userData) {
      const workflowStatusinResponse = userData?.response?.data
        ?.workflowAndNodeStatus as WorkflowStatusSchema;

      switch (workflowStatusinResponse?.workflowRunStatus) {
        case NODE_STATUS.SUCCESS:
          toast({
            title: isWorkflowPartiallyRunning
              ? "Node execution completed"
              : "Workflow run completed successfully",
            status: "success",
          });
          fetchAndUpdateWorkflow().catch((e) => console.error(e));
          dispatch(setworkflowStatus(null));
          break;
        case NODE_STATUS.FAILED:
          if (wfRunStatus === NODE_STATUS.RUNNING) {
            // if complete wf was running, show wf failed toast
            toast({
              title: "Workflow Run Failed",
              status: "error",
            });
          } else if (wfRunStatus === NODE_STATUS.PARTIALLY_RUNNING) {
            // if partial wf was running, show node run failed toast
            toast({
              title: "Node Run Failed",
              status: "error",
            });
          }
          fetchAndUpdateWorkflow().catch((e) => console.error(e));
          dispatch(setworkflowStatus(null));
          break;
        case NODE_STATUS.DEFAULT:
        case NODE_STATUS.CANCELLED:
          if (wfRunStatus === NODE_STATUS.RUNNING) {
            toast({
              title: "Run terminated successfully",
            });
            dispatch(setEditingAllowed(true));
          }
          // had added this to refetch the run_status api
          // since there was a delay in updating the node status in  the run_status response
          // NOTE: commenting it out now becuase run_status api is supposed to give updated status
          // refetch();
          fetchAndUpdateWorkflow().catch((e) => console.error(e));
          dispatch(setworkflowStatus(null));
          break;
        default:
          dispatch(setworkflowStatus(workflowStatusinResponse));
          break;
      }
      dispatch(
        setWorkflowRunStatus(workflowStatusinResponse.workflowRunStatus)
      );
    }
  }, [userData]);

  useEffect(() => {
    if (isError && error) {
      console.log("error", failureCountRef.current);
      failureCountRef.current += 1;
      if (failureCountRef.current >= 5) {
        fetchAndUpdateWorkflow().catch((e) => console.error(e));
        failureCountRef.current = 0; // Reset counter after handling
      }
    } else if (!isError && !error) {
      failureCountRef.current = 0;
    }
  }, [isError]);

  const openVersionHistoryPanel = () => {
    dispatch(setPreviewMode(true));
    dispatch(showPanel({ panel: WORKFLOW_PANELS.VersionHistoryPanel }));
  };

  const saveWF = async () => {
    try {
      const resp = await saveWorkflow({
        analysisId: params.analysisId!,
        workflow: ConvertFlowtoWorkflowPayload(toObject()!),
        workflowId: params.editorId!,
      }).unwrap();
      const wf = resp.response.data!.workflows[0];
      dispatch(setWorkflowRunId(wf.workflowRunId as string));
      dispatch(setWorkflowVersionTagId(wf.workflowVersionTagId));
      dispatch(setReferenceRunId(wf.referenceRunId as string));
      return true;
    } catch (err) {
      return false;
    }
  };

  const onEndEditClick = async () => {
    try {
      const saved = await saveWF();
      if (!saved) throw new Error("Failed to save workflow");
      await endEditAccess({
        analysisId: params.analysisId!,
      }).unwrap();

      toast({
        title: "Session closed",
        status: ToastType.Success,
      });
      dispatch(hideAllPanels());
      dispatch(setEditingAllowed(false));
      dispatch(
        setCurrentWfActiveUser({
          user: null,
          status: "",
          isCurrentUser: false,
          timeSinceLastActivity: null,
        })
      );
    } catch (err) {
      toast({
        title: "Failed to end edit access",
        status: ToastType.Error,
      });
    }
  };

  return (
    <Stack
      className={clsx(
        "absolute top-5 left-5 border border-gray-400",
        "rounded-sm bg-white p-1 shadow-noblur",
        "flex gap-1"
      )}
      direction="row"
    >
      {showLockedMessage ? (
        <ViewModeOptions hasEditAccess={hasEditAccess} />
      ) : (
        <>
          {isPreviewModeOn ? (
            <PreviewModeOptions />
          ) : (
            <>
              <IconButton
                aria-label="Version History"
                colorScheme="dark"
                icon={<MdHistory />}
                isDisabled={disableHistoryButton}
                onClick={openVersionHistoryPanel}
                title={
                  disableHistoryButton
                    ? "Few nodes are running"
                    : "Version History"
                }
                variant={"ghost"}
              />
              <div className="w-0.5 bg-gray-200" />
              <RunAndLogButtons
                hasEditAccess={hasEditAccess}
                nodes={nodes}
                edges={edges}
              />
              {isCurrentUserEditing && isEditingAllowed && (
                <Button
                  className="!bg-white !text-red-600"
                  colorScheme="red"
                  isDisabled={isLoading}
                  isLoading={isLoading}
                  leftIcon={
                    <MdsEditOffRound className="stroke-[22]" fontSize={14} />
                  }
                  onClick={onEndEditClick}
                  size="sm"
                  variant={"outline"}
                >
                  Stop Editing
                </Button>
              )}
            </>
          )}
        </>
      )}
    </Stack>
  );
};
